import React, { useMemo, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { VideoSource } from "../../Enums/enums";
import { buildBatchGroups, cardsNotEdited, flattenItems } from "./batchItems";
import BatchEditorDialog from "./BatchEditorDialog";

/**
 * Step 3's batch-editing bar: ONE button that opens every video in the batch
 * editor (user request 2026-10-13: it used to take two clicks, "Set all to
 * Edited" and then "Edit all in one editor").
 *
 * Cards still on the original video are switched to Edited on the way in. A
 * card where Local upload or Duplicate was chosen is left as it is — that is a
 * deliberate choice, and duplicates still show in the editor as read-only tabs.
 */
export default function BatchEditToolbar({ selectedBrands, brandVideos, brandPlatforms, platformConfigs, onConfigChange, onRemoveVideo }) {
  const [editorOpen, setEditorOpen] = useState(false);

  const groups = useMemo(
    () => buildBatchGroups(selectedBrands, brandVideos, brandPlatforms, platformConfigs),
    [selectedBrands, brandVideos, brandPlatforms, platformConfigs],
  );
  const editedCount = useMemo(() => flattenItems(groups).length, [groups]);
  // Cards the button switches to Edited: those still on the original video.
  const toSwitch = useMemo(
    () =>
      cardsNotEdited(selectedBrands, brandVideos, brandPlatforms, platformConfigs).filter(
        (c) => !c.source || c.source === VideoSource.ORIGINAL,
      ),
    [selectedBrands, brandVideos, brandPlatforms, platformConfigs],
  );
  const total = editedCount + toSwitch.length;

  // Cards whose thumbnail is still None. "Set all thumbnails to Original"
  // switches them to the video's own thumbnail; a Custom one is left as is.
  const noThumbnail = useMemo(() => {
    const out = [];
    selectedBrands.forEach(({ name: brandName }) =>
      (brandVideos[brandName] || []).forEach((video) =>
        (brandPlatforms[brandName] || []).forEach((platform) => {
          const cfg = platformConfigs[brandName]?.[video.unique_key]?.[platform];
          if ((cfg?.thumbnailSource || "none") === "none") out.push({ brandName, wgid: video.unique_key, platform });
        }),
      ),
    );
    return out;
  }, [selectedBrands, brandVideos, brandPlatforms, platformConfigs]);

  const setThumbnailsToOriginal = () => {
    noThumbnail.forEach((c) =>
      onConfigChange(c.brandName, c.wgid, c.platform, { thumbnailSource: "original", thumbnailFile: null, thumbnailPreview: null }),
    );
  };

  const openAll = () => {
    // Remember each card's own choice, so removing it from the batch editor
    // puts it back the way it was.
    toSwitch.forEach((c) =>
      onConfigChange(c.brandName, c.wgid, c.platform, { source: VideoSource.EDITED, sourceBeforeBatch: c.source || null }),
    );
    setEditorOpen(true);
  };

  if (total === 0 && noThumbnail.length === 0) return null;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2, display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, gap: 1.5 }}
      >
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle2" fontWeight={600}>
            Batch editing
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Edit every video × platform in one screen.
            {toSwitch.length > 0 && ` ${toSwitch.length} card${toSwitch.length !== 1 ? "s are" : " is"} switched to Edited when you open it.`}
          </Typography>
        </Box>
        {/*
          Where "Set all to Edited" was (user request 2026-10-13): every card's
          thumbnail from None to the video's Original in one click. A card set
          to Custom keeps its own.
        */}
        <Button
          variant="outlined"
          startIcon={<PhotoLibraryIcon />}
          onClick={setThumbnailsToOriginal}
          disabled={noThumbnail.length === 0}
          data-testid="batch-thumbnails-original"
          size="small"
          sx={{ minHeight: 32, py: 0.25, px: 1.25, fontSize: 12.5, textTransform: "none", fontWeight: 600, borderRadius: 1.5, whiteSpace: "nowrap", "& .MuiButton-startIcon svg": { fontSize: 16 } }}
        >
          {noThumbnail.length === 0 ? "All thumbnails set" : `Set all thumbnails to Original (${noThumbnail.length})`}
        </Button>
        <Button
          variant="contained"
          startIcon={<MovieFilterIcon />}
          onClick={openAll}
          disabled={total === 0}
          data-testid="batch-edit-all"
          sx={{
            minHeight: 40,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2.5,
            background: total > 0 ? "linear-gradient(135deg, #312e81, #4338ca)" : undefined,
          }}
        >
          Edit all in one editor ({total})
        </Button>
      </Paper>

      <BatchEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        groups={groups}
        onConfigChange={onConfigChange}
        onRemoveVideo={onRemoveVideo}
      />
    </>
  );
}
